import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import { API_URL, STORAGE_URL } from "../../utils/constants";
import { tField } from "../../utils/helpers";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { SelectCustomStyles } from "../../utils/styles";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  IoSearchOutline, IoClose, IoOptions,
  IoLocationOutline, IoChevronForward
} from "react-icons/io5";
import "./styles.css";

const TRIPOLI = [32.9, 13.19];

delete L.Icon.Default.prototype._getIconUrl;

function makeMarkerIcon(price, selected) {
  const label = price ? Number(price).toLocaleString() + ' د.ل' : '📍';
  return new L.DivIcon({
    html: `<div class="msp-marker${selected ? ' msp-marker--active' : ''}">${label}</div>`,
    className: '',
    iconAnchor: [0, 20],
    iconSize: null,
  });
}

function MapController({ mapRef, onMoveEnd, onReady }) {
  const map = useMap();
  useEffect(() => {
    mapRef.current = map;
    if (onReady) onReady(map);
  }, [map]);
  useMapEvents({ moveend: onMoveEnd });
  return null;
}

function MapSearchPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const mapRef = useRef(null);
  const skipNextMoveEnd = useRef(true); // suppress moveend fired by initial auto-center
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSearchBtn, setShowSearchBtn] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({ property_for: '', category_id: '' });
  const [draftFilters, setDraftFilters] = useState({ property_for: '', category_id: '' });

  useEffect(() => {
    fetch(API_URL + '/categories', { headers: { 'Content-Type': 'application/json' } })
      .then(r => r.json())
      .then(d => { if (d.success) setCategories(d.categories); });
  }, []);

  const getRadiusKm = () => {
    if (!mapRef.current) return 10;
    const bounds = mapRef.current.getBounds();
    const center = mapRef.current.getCenter();
    return center.distanceTo(bounds.getNorthEast()) / 1000;
  };

  const doSearch = (lat, lng, radius, currentFilters) => {
    setLoading(true);
    setShowSearchBtn(false);
    const qs = new URLSearchParams({ latitude: lat, longitude: lng, radius: radius.toFixed(2) });
    if (currentFilters.property_for) qs.set('property_for', currentFilters.property_for);
    if (currentFilters.category_id)  qs.set('category_id',  currentFilters.category_id);

    fetch(`${API_URL}/properties/map?${qs}`, {
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
    })
    .then(r => r.json())
    .then(d => { if (d.success) setProperties(d.properties || []); })
    .catch(() => {})
    .finally(() => setLoading(false));
  };

  // Called once after MapContainer mounts — safe to call map.setView here
  const handleMapReady = useCallback((map) => {
    const initFilters = { property_for: '', category_id: '' };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          skipNextMoveEnd.current = true;
          map.setView([coords.latitude, coords.longitude], 14);
          doSearch(coords.latitude, coords.longitude, 5, initFilters);
        },
        () => doSearch(TRIPOLI[0], TRIPOLI[1], 10, initFilters)
      );
    } else {
      doSearch(TRIPOLI[0], TRIPOLI[1], 10, initFilters);
    }
  }, []);

  const handleSearchThisArea = () => {
    if (!mapRef.current) return;
    const { lat, lng } = mapRef.current.getCenter();
    doSearch(lat, lng, getRadiusKm(), filters);
  };

  const handleMoveEnd = useCallback(() => {
    if (skipNextMoveEnd.current) {
      skipNextMoveEnd.current = false;
      return;
    }
    setShowSearchBtn(true);
    setSelectedProperty(null);
  }, []);

  const applyFilters = (newFilters) => {
    setFilterOpen(false);
    setFilters(newFilters);
    if (!mapRef.current) return;
    const { lat, lng } = mapRef.current.getCenter();
    doSearch(lat, lng, getRadiusKm(), newFilters);
  };

  const hasActiveFilters = filters.property_for || filters.category_id;

  const categoryOptions = [
    { value: '', label: t('home.search.fields.category.all') },
    ...categories.map(c => ({ value: c.id, label: tField(c, 'name') }))
  ];

  const purposeOptions = [
    { value: '',         label: 'الكل' },
    { value: 'rent',     label: 'للإيجار' },
    { value: 'sale',     label: 'للبيع' },
    { value: 'exchange', label: 'للبدل' },
  ];

  const openFilter = () => { setDraftFilters(filters); setFilterOpen(true); };

  return (
    <MainLayout isTopBar screenTitle="البحث بالخريطة">
      <div className="msp-wrapper">

        {/* ── Map ── */}
        <MapContainer
          center={TRIPOLI}
          zoom={12}
          style={{ width: '100%', height: '100%' }}
          zoomControl={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />
          <MapController mapRef={mapRef} onMoveEnd={handleMoveEnd} onReady={handleMapReady} />
          {properties.map(p =>
            p.latitude && p.longitude ? (
              <Marker
                key={p.id + (selectedProperty?.id === p.id ? '-s' : '')}
                position={[parseFloat(p.latitude), parseFloat(p.longitude)]}
                icon={makeMarkerIcon(p.price, selectedProperty?.id === p.id)}
                eventHandlers={{ click: () => setSelectedProperty(p) }}
              />
            ) : null
          )}
        </MapContainer>

        {/* Floating filter button */}
        <button
          className={'msp-filter-fab' + (hasActiveFilters ? ' msp-filter-fab--active' : '')}
          onClick={openFilter}
          aria-label="فلترة"
        >
          <IoOptions size={22} />
          {hasActiveFilters && <span className="msp-filter-fab__dot" />}
        </button>

        {/* Loading pill */}
        {loading && (
          <div className="msp-loading-pill">
            <span className="spinner-border spinner-border-sm" />
            جاري البحث...
          </div>
        )}

        {/* Count badge */}
        {!loading && properties.length > 0 && !showSearchBtn && (
          <div className="msp-count-badge">
            <IoLocationOutline size={13} />
            {properties.length} عقار
          </div>
        )}

        {/* Search this area button */}
        {showSearchBtn && !loading && (
          <div className="msp-search-area-wrap">
            <button className="msp-search-area-btn" onClick={handleSearchThisArea}>
              <IoSearchOutline size={15} />
              ابحث في هذه المنطقة
            </button>
          </div>
        )}

        {/* Property bottom sheet */}
        {selectedProperty && (
          <div className="msp-sheet" onClick={() => navigate('/property/' + selectedProperty.id)}>
            <button
              className="msp-sheet__close"
              onClick={e => { e.stopPropagation(); setSelectedProperty(null); }}
            >
              <IoClose size={17} />
            </button>

            <div className="msp-sheet__inner">
              <img
                className="msp-sheet__img"
                src={selectedProperty.images?.[0]?.image_path
                  ? STORAGE_URL + selectedProperty.images[0].image_path
                  : require('../../assets/images/default-office.png')}
                alt=""
              />
              <div className="msp-sheet__info">
                <p className="msp-sheet__title">{tField(selectedProperty, 'title')}</p>
                {selectedProperty.city && (
                  <p className="msp-sheet__city">
                    <IoLocationOutline size={13} />
                    {tField(selectedProperty.city, 'name')}
                  </p>
                )}
                {selectedProperty.price && (
                  <p className="msp-sheet__price">
                    {Number(selectedProperty.price).toLocaleString()}
                    <span> د.ل</span>
                  </p>
                )}
              </div>
              <IoChevronForward size={18} className="msp-sheet__arrow" />
            </div>
          </div>
        )}

      </div>

      {/* ── Filter modal ── */}
      {filterOpen && (
        <div className="msp-filter-overlay" onClick={() => setFilterOpen(false)}>
          <div className="msp-filter-modal" onClick={e => e.stopPropagation()}>

            <div className="msp-filter-modal__header">
              <span className="msp-filter-modal__title">فلترة النتائج</span>
              <button className="msp-filter-modal__close" onClick={() => setFilterOpen(false)}>
                <IoClose size={20} />
              </button>
            </div>

            <div className="msp-filter-modal__body">
              <div className="mb-4">
                <label className="msp-filter-label">الغرض</label>
                <div className="msp-filter-chips">
                  {purposeOptions.map(o => (
                    <button
                      key={o.value}
                      type="button"
                      className={'msp-filter-chip' + (draftFilters.property_for === o.value ? ' active' : '')}
                      onClick={() => setDraftFilters(f => ({ ...f, property_for: o.value }))}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-2">
                <label className="msp-filter-label">نوع العقار</label>
                <Select
                  placeholder="الكل"
                  options={categoryOptions}
                  value={draftFilters.category_id
                    ? categoryOptions.find(o => o.value === draftFilters.category_id) || categoryOptions[0]
                    : categoryOptions[0]}
                  onChange={opt => setDraftFilters(f => ({ ...f, category_id: opt?.value || '' }))}
                  styles={SelectCustomStyles}
                  isSearchable={false}
                />
              </div>
            </div>

            <div className="msp-filter-modal__footer">
              <button
                className="msp-filter-reset"
                onClick={() => setDraftFilters({ property_for: '', category_id: '' })}
              >
                إعادة تعيين
              </button>
              <button className="msp-filter-apply" onClick={() => applyFilters(draftFilters)}>
                <IoSearchOutline size={15} />
                تطبيق
              </button>
            </div>

          </div>
        </div>
      )}
    </MainLayout>
  );
}

export default MapSearchPage;
